/**
 * Regenerable — Shared primitive for any AI re-run region.
 *
 * Authority: docs/contracts/DESIGN_IMPLEMENTATION_CONTRACT.md Section 9.
 * Tokens:    DS_COLORS, DS_SPACING, DS_MOTION, DS_TYPOGRAPHY (ui-invariants.js).
 * Copy:      copy.shared.regenerating (content/copy.js).
 * Composes:  PipelineProgressCard for the in-flight tracker overlay.
 *
 * Behaviour:
 *   - While isRegenerating is true, children dim to DS_MOTION.regen.dimmedOpacity
 *     over DS_MOTION.regen.fadeMs and a Stripe-style progress tracker renders
 *     centred over them.
 *   - When isRegenerating flips false, children restore to opacity 1 over the
 *     same fade. Any descendant whose data-regen-key attribute matches an entry
 *     in changedKeys receives a 1px violet edge glow for glowHold + glowDissolve.
 *   - On a step with state === 'error', the tracker renders the failed circle
 *     and, if onRegenerate is provided, a ghost "Retry" action next to the
 *     label.
 *
 * Usage:
 *   <Regenerable
 *     isRegenerating={running}
 *     steps={STEPS}
 *     changedKeys={['verdict', 'score']}
 *     regenerateLabel={copy.dealEvaluator.regenerating}
 *     onRegenerate={retry}
 *   >
 *     <div data-regen-key="verdict">…</div>
 *     <div data-regen-key="score">…</div>
 *   </Regenerable>
 */

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

import {
  DS_COLORS,
  DS_SPACING,
  DS_TYPOGRAPHY,
  DS_MOTION,
} from '@/config/ui-invariants';
import { copy } from '@/content/copy';
import PipelineProgressCard from '@/components/shared/PipelineProgressCard';

function deriveCurrentPhase(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  const active = steps.find((s) => s.state === 'active');
  if (active) return active.key;
  const error = steps.find((s) => s.state === 'error');
  if (error) return error.key;
  const firstIdle = steps.find((s) => s.state === 'idle');
  if (firstIdle) return firstIdle.key;
  return steps[steps.length - 1].key;
}

function deriveTrackerState(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return 'not_started';
  if (steps.some((s) => s.state === 'error')) return 'failed';
  if (steps.every((s) => s.state === 'complete')) return 'complete';
  if (steps.some((s) => s.state === 'active')) return 'running';
  return 'not_started';
}

export function Regenerable({
  isRegenerating = false,
  onRegenerate,
  steps = [],
  regenerateLabel = copy.shared.regenerating,
  changedKeys = [],
  children,
}) {
  const containerRef = useRef(null);
  const [glowing, setGlowing] = useState(new Set());
  const lastIsRegenerating = useRef(isRegenerating);

  // When a regeneration cycle finishes (true → false), start the edge-glow
  // phase for every child whose data-regen-key is in changedKeys. The glow
  // holds for DS_MOTION.regen.glowHold then dissolves over glowDissolve.
  useEffect(() => {
    if (lastIsRegenerating.current && !isRegenerating && changedKeys.length > 0) {
      const keys = new Set(changedKeys);
      setGlowing(keys);
      const total = DS_MOTION.regen.glowHold + DS_MOTION.regen.glowDissolve;
      const t = setTimeout(() => setGlowing(new Set()), total);
      lastIsRegenerating.current = isRegenerating;
      return () => clearTimeout(t);
    }
    lastIsRegenerating.current = isRegenerating;
    return undefined;
  }, [isRegenerating, changedKeys]);

  // Walk the DOM inside containerRef and toggle a glow className on elements
  // whose data-regen-key is in the glowing set. Kept as a side effect so the
  // primitive works with arbitrary children shapes without forcing callers to
  // wrap each changed element in a Regenerable child component.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;
    const targets = root.querySelectorAll('[data-regen-key]');
    const handles = [];
    targets.forEach((el) => {
      const key = el.getAttribute('data-regen-key');
      if (glowing.has(key)) {
        el.style.transition = `box-shadow ${DS_MOTION.regen.glowDissolve}ms ease-out`;
        el.style.boxShadow = `0 0 0 1px ${DS_COLORS.ai.action}`;
        const holdT = setTimeout(() => {
          el.style.boxShadow = 'none';
        }, DS_MOTION.regen.glowHold);
        handles.push(holdT);
      } else if (!isRegenerating) {
        el.style.boxShadow = '';
      }
    });
    return () => handles.forEach(clearTimeout);
  }, [glowing, isRegenerating]);

  const trackerState = deriveTrackerState(steps);
  const currentPhase = deriveCurrentPhase(steps);
  const hasError = trackerState === 'failed';

  return (
    <div
      ref={containerRef}
      aria-busy={isRegenerating || undefined}
      style={{ position: 'relative' }}
    >
      <motion.div
        animate={{ opacity: isRegenerating ? DS_MOTION.regen.dimmedOpacity : 1 }}
        transition={{ duration: DS_MOTION.regen.fadeMs / 1000, ease: 'easeOut' }}
      >
        {children}
      </motion.div>

      <AnimatePresence>
        {isRegenerating && (
          <motion.div
            role="status"
            aria-live="polite"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DS_MOTION.regen.fadeMs / 1000, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: DS_SPACING.cardGap,
              pointerEvents: hasError && onRegenerate ? 'auto' : 'none',
            }}
          >
            {steps.length > 0 && (
              <PipelineProgressCard
                steps={steps.map((s) => ({ key: s.key, label: s.label }))}
                currentPhase={currentPhase}
                state={trackerState}
              />
            )}
            <div
              style={{
                ...DS_TYPOGRAPHY.actionBtn,
                color: DS_COLORS.text.brand,
              }}
            >
              {regenerateLabel}
            </div>
            {hasError && onRegenerate && (
              <button
                type="button"
                onClick={onRegenerate}
                style={{
                  ...DS_TYPOGRAPHY.actionBtn,
                  color: DS_COLORS.text.brand,
                  background: DS_COLORS.surface.white,
                  border: `1.5px solid ${DS_COLORS.border.default}`,
                  borderRadius: 6,
                  padding: '8px 16px',
                  minHeight: DS_SPACING.minTouchTarget,
                  cursor: 'pointer',
                }}
              >
                {copy.shared.retry}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

Regenerable.propTypes = {
  isRegenerating: PropTypes.bool,
  onRegenerate: PropTypes.func,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      state: PropTypes.oneOf(['idle', 'active', 'complete', 'error']).isRequired,
    }),
  ),
  regenerateLabel: PropTypes.string,
  changedKeys: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
};

export default Regenerable;
